import type { ColorSystemOptions, PaletteColor } from '@mui/material/styles'

/** Same shape as MUI `Channels` from `createPalette` (not exported from `@mui/material/styles` entry). */
export type Channels = {
  mainChannel: string
  lightChannel: string
  darkChannel: string
  contrastTextChannel: string
}

import { createPaletteChannel, varAlpha } from 'minimal-shared/utils'
import { themeConfig } from '../theme-config'
import type { ThemeColorScheme } from '../types'

// ----------------------------------------------------------------------

/**
 * TypeScript (type definition and extension)
 * @to {@link file://./../extend-theme-types.d.ts}
 */

// Keys for the palette colors
export type PaletteColorKey = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'

// Palette color without additional channels
export type PaletteColorNoChannels = Omit<PaletteColor, 'lighterChannel' | 'darkerChannel'>

// Palette color with additional channels
export type PaletteColorWithChannels = PaletteColor & Channels

// Extended common colors
export type CommonColorsExtend = {
  whiteChannel: string
  blackChannel: string
}

// Extended text colors
export type TypeTextExtend = {
  disabledChannel: string
}

// Extended background colors
export type TypeBackgroundExtend = {
  neutral: string
  neutralChannel: string
}

// Extended palette colors
export type PaletteColorExtend = {
  lighter: string
  darker: string
  lighterChannel: string
  darkerChannel: string
}

// Extended grey channels
export type GreyExtend = {
  '50Channel': string
  '100Channel': string
  '200Channel': string
  '300Channel': string
  '400Channel': string
  '500Channel': string
  '600Channel': string
  '700Channel': string
  '800Channel': string
  '900Channel': string
}

// ----------------------------------------------------------------------

// Primary color
export const primary = {
  '50': '#e7f3ee',
  '100': '#c6e2d3',
  '200': '#a4d1b9',
  '300': '#88be9f',
  '400': '#77af8e',
  '500': '#6da07f',
  '600': '#659374',
  '700': '#5c8267',
  '800': '#53725b',
  '900': '#435545',
  lighter: '#EEF6EC',
  light: '#B3CCB2',
  main: '#435545',
  dark: '#213D29',
  darker: '#0C281A',
  contrastText: '#FFFFFF',
  '50Channel': '231 243 238',
  '100Channel': '198 226 211',
  '200Channel': '164 209 185',
  '300Channel': '136 190 159',
  '400Channel': '119 175 142',
  '500Channel': '109 160 127',
  '600Channel': '101 147 116',
  '700Channel': '92 130 103',
  '800Channel': '83 114 91',
  '900Channel': '67 85 69',
  lighterChannel: '238 246 236',
  lightChannel: '179 204 178',
  mainChannel: '67 85 69',
  darkChannel: '33 61 41',
  darkerChannel: '12 40 26',
  contrastTextChannel: '255 255 255',
}

// Secondary color
export const secondary = {
  '50': '#f2e5ff',
  '100': '#dabffe',
  '200': '#c192fe',
  '300': '#a661ff',
  '400': '#8f33ff',
  '500': '#7500fd',
  '600': '#6800f7',
  '700': '#5400ef',
  '800': '#3c00eb',
  '900': '#0000dd',
  lighter: '#EFD6FF',
  light: '#C684FF',
  main: '#8E33FF',
  dark: '#5119B7',
  darker: '#27097A',
  contrastText: '#FFFFFF',
  '50Channel': '242 229 255',
  '100Channel': '218 191 254',
  '200Channel': '193 146 254',
  '300Channel': '166 97 255',
  '400Channel': '143 51 255',
  '500Channel': '117 0 253',
  '600Channel': '104 0 247',
  '700Channel': '84 0 239',
  '800Channel': '60 0 235',
  '900Channel': '0 0 221',
  lighterChannel: '239 214 255',
  lightChannel: '198 132 255',
  mainChannel: '142 51 255',
  darkChannel: '81 25 183',
  darkerChannel: '39 9 122',
  contrastTextChannel: '255 255 255',
}

// Info color
export const info = {
  '50': '#e2f7ff',
  '100': '#b6ecfd',
  '200': '#86e0fc',
  '300': '#56d3f7',
  '400': '#34caf2',
  '500': '#25c1ec',
  '600': '#1db1d8',
  '700': '#0f9cbc',
  '800': '#0188a3',
  '900': '#006676',
  lighter: '#5BE1E3',
  light: '#32BAC7',
  main: '#0188A3',
  dark: '#006A8C',
  darker: '#004F75',
  contrastText: '#FFFFFF',
  '50Channel': '226 247 255',
  '100Channel': '182 236 253',
  '200Channel': '134 224 252',
  '300Channel': '86 211 247',
  '400Channel': '52 202 242',
  '500Channel': '37 193 236',
  '600Channel': '29 177 216',
  '700Channel': '15 156 188',
  '800Channel': '1 136 163',
  '900Channel': '0 102 118',
  lighterChannel: '91 225 227',
  lightChannel: '50 186 199',
  mainChannel: '1 136 163',
  darkChannel: '0 106 140',
  darkerChannel: '0 79 117',
  contrastTextChannel: '255 255 255',
}

// Success color
export const success = {
  '50': '#e5f6ec',
  '100': '#c1e8d0',
  '200': '#99d9b3',
  '300': '#6dcb95',
  '400': '#48c07e',
  '500': '#13b468',
  '600': '#06a55d',
  '700': '#009351',
  '800': '#008145',
  '900': '#006231',
  lighter: '#97F6AF',
  light: '#37C979',
  main: '#06A55E',
  dark: '#048D5E',
  darker: '#03765A',
  contrastText: '#FFFFFF',
  '50Channel': '229 246 236',
  '100Channel': '193 232 208',
  '200Channel': '153 217 179',
  '300Channel': '109 203 149',
  '400Channel': '72 192 126',
  '500Channel': '19 180 104',
  '600Channel': '6 165 93',
  '700Channel': '0 147 81',
  '800Channel': '0 129 69',
  '900Channel': '0 98 49',
  lighterChannel: '151 246 175',
  lightChannel: '55 201 121',
  mainChannel: '6 165 94',
  darkChannel: '4 141 94',
  darkerChannel: '3 118 90',
  contrastTextChannel: '255 255 255',
}

// Warning color
export const warning = {
  '50': '#fff8e1',
  '100': '#ffecb3',
  '200': '#ffe082',
  '300': '#ffd54f',
  '400': '#ffca28',
  '500': '#ffc107',
  '600': '#ffb300',
  '700': '#ffa000',
  '800': '#ff8f00',
  '900': '#ff6f00',
  lighter: '#F5D462',
  light: '#ECBE3B',
  main: '#E0A000',
  dark: '#C08300',
  darker: '#A16900',
  contrastText: '#000000',
  '50Channel': '255 248 225',
  '100Channel': '255 236 179',
  '200Channel': '255 224 130',
  '300Channel': '255 213 79',
  '400Channel': '255 202 40',
  '500Channel': '255 193 7',
  '600Channel': '255 179 0',
  '700Channel': '255 160 0',
  '800Channel': '255 143 0',
  '900Channel': '255 111 0',
  lighterChannel: '245 212 98',
  lightChannel: '236 190 59',
  mainChannel: '224 160 0',
  darkChannel: '192 131 0',
  darkerChannel: '161 105 0',
  contrastTextChannel: '0 0 0',
}

// Error color
export const error = {
  '50': '#ffebee',
  '100': '#feccd3',
  '200': '#ee989b',
  '300': '#e47074',
  '400': '#ef4e52',
  '500': '#f43b39',
  '600': '#e53138',
  '700': '#d32632',
  '800': '#c61e2a',
  '900': '#b70e1e',
  lighter: '#F1887A',
  light: '#E45B58',
  main: '#D32631',
  dark: '#B51B33',
  darker: '#971333',
  contrastText: '#FFFFFF',
  '50Channel': '255 235 238',
  '100Channel': '254 204 211',
  '200Channel': '238 152 155',
  '300Channel': '228 112 116',
  '400Channel': '239 78 82',
  '500Channel': '244 59 57',
  '600Channel': '229 49 56',
  '700Channel': '211 38 50',
  '800Channel': '198 30 42',
  '900Channel': '183 14 30',
  lighterChannel: '241 136 122',
  lightChannel: '228 91 88',
  mainChannel: '211 38 49',
  darkChannel: '181 27 51',
  darkerChannel: '151 19 51',
  contrastTextChannel: '255 255 255',
}

// Common color
export const common = {
  black: '#000000',
  white: '#FFFFFF',
  blackChannel: '0 0 0',
  whiteChannel: '255 255 255',
  background: '#fff',
  onBackground: '#000',
  backgroundChannel: '255 255 255',
  onBackgroundChannel: '0 0 0',
}

// Grey color
export const grey = {
  '50': '#FCFDFD',
  '100': '#F9FAFB',
  '200': '#F4F6F8',
  '300': '#DFE3E8',
  '400': '#C4CDD5',
  '500': '#919EAB',
  '600': '#637381',
  '700': '#454F5B',
  '800': '#1C252E',
  '900': '#141A21',
  '50Channel': '252 253 253',
  '100Channel': '249 250 251',
  '200Channel': '244 246 248',
  '300Channel': '223 227 232',
  '400Channel': '196 205 213',
  '500Channel': '145 158 171',
  '600Channel': '99 115 129',
  '700Channel': '69 79 91',
  '800Channel': '28 37 46',
  '900Channel': '20 26 33',
  A100: '#f5f5f5',
  A200: '#eeeeee',
  A400: '#bdbdbd',
  A700: '#616161',
}

// Text color
// Hors échelle des gris : secondary et disabled visent 4,5:1 minimum sur le blanc comme sur les fonds teintés.
export const text = {
  light: createPaletteChannel({ primary: grey[800], secondary: '#4B5763', disabled: '#616E7B' }),
  dark: createPaletteChannel({ primary: '#FFFFFF', secondary: '#B0BAC4', disabled: grey[500] }),
}

// Background color
export const background = {
  light: createPaletteChannel({ paper: '#FFFFFF', default: '#FFFFFF', neutral: '#EEF1F4' }),
  dark: createPaletteChannel({ paper: grey[800], default: grey[900], neutral: '#28323D' }),
}

// Base action color
export const baseAction = {
  hover: varAlpha(grey['500Channel'], 0.08),
  selected: varAlpha(grey['500Channel'], 0.16),
  focus: varAlpha(grey['500Channel'], 0.24),
  disabled: varAlpha(grey['500Channel'], 0.8),
  disabledBackground: varAlpha(grey['500Channel'], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
}

// Action color
export const action = {
  light: { ...baseAction, active: grey[600] },
  dark: { ...baseAction, active: grey[500] },
}

// ----------------------------------------------------------------------

// Base palette
export const basePalette = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  divider: 'rgba(145 158 171 / 0.32)',
  action: {
    hover: 'rgba(145 158 171 / 0.08)',
    selected: 'rgba(145 158 171 / 0.16)',
    focus: 'rgba(145 158 171 / 0.24)',
    disabled: 'rgba(145 158 171 / 0.8)',
    disabledBackground: 'rgba(145 158 171 / 0.24)',
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
    active: '#637381',
    selectedOpacity: 0.08,
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
    activeChannel: '99 115 129',
    selectedChannel: '145',
  },
  text: {
    primary: '#1C252E',
    secondary: '#4B5763',
    disabled: '#616E7B',
    primaryChannel: '28 37 46',
    secondaryChannel: '75 87 99',
    disabledChannel: '97 110 123',
  },
  background: {
    paper: '#FFFFFF',
    default: '#FFFFFF',
    neutral: '#EEF1F4',
    paperChannel: '255 255 255',
    defaultChannel: '255 255 255',
    neutralChannel: '238 241 244',
  },
  contrastThreshold: 3,
  tonalOffset: 0.2,
  Alert: {
    errorColor: 'rgb(91, 36, 35)',
    infoColor: 'rgb(20, 74, 79)',
    successColor: 'rgb(22, 80, 48)',
    warningColor: 'rgb(94, 76, 23)',
    errorFilledBg: 'var(--palette-error-main, #D32631)',
    infoFilledBg: 'var(--palette-info-main, #0188A3)',
    successFilledBg: 'var(--palette-success-main, #06A55E)',
    warningFilledBg: 'var(--palette-warning-main, #E0A000)',
    errorFilledColor: '#fff',
    infoFilledColor: '#fff',
    successFilledColor: '#fff',
    warningFilledColor: 'rgba(0, 0, 0, 0.87)',
    errorStandardBg: 'rgb(252, 238, 238)',
    infoStandardBg: 'rgb(234, 248, 249)',
    successStandardBg: 'rgb(235, 249, 241)',
    warningStandardBg: 'rgb(253, 248, 235)',
    errorIconColor: 'var(--palette-error-main, #D32631)',
    infoIconColor: 'var(--palette-info-main, #0188A3)',
    successIconColor: 'var(--palette-success-main, #06A55E)',
    warningIconColor: 'var(--palette-warning-main, #E0A000)',
  },
  AppBar: {
    defaultBg: 'var(--palette-grey-100, #F9FAFB)',
  },
  Avatar: {
    defaultBg: 'var(--palette-grey-400, #C4CDD5)',
  },
  Button: {
    inheritContainedBg: 'var(--palette-grey-300, #DFE3E8)',
    inheritContainedHoverBg: 'var(--palette-grey-A100, #f5f5f5)',
  },
  Chip: {
    defaultBorder: 'var(--palette-grey-400, #C4CDD5)',
    defaultAvatarColor: 'var(--palette-grey-700, #454F5B)',
    defaultIconColor: 'var(--palette-grey-700, #454F5B)',
  },
  FilledInput: {
    bg: 'rgba(0, 0, 0, 0.06)',
    hoverBg: 'rgba(0, 0, 0, 0.09)',
    disabledBg: 'rgba(0, 0, 0, 0.12)',
  },
  LinearProgress: {
    primaryBg: 'rgb(183, 190, 184)',
    secondaryBg: 'rgb(212, 177, 255)',
    errorBg: 'rgb(238, 172, 176)',
    infoBg: 'rgb(158, 209, 220)',
    successBg: 'rgb(160, 220, 193)',
    warningBg: 'rgb(243, 218, 158)',
  },
  Skeleton: {
    bg: 'rgba(var(--palette-text-primaryChannel, 28 37 46) / 0.11)',
  },
  Slider: {
    primaryTrack: 'rgb(183, 190, 184)',
    secondaryTrack: 'rgb(212, 177, 255)',
    errorTrack: 'rgb(238, 172, 176)',
    infoTrack: 'rgb(158, 209, 220)',
    successTrack: 'rgb(160, 220, 193)',
    warningTrack: 'rgb(243, 218, 158)',
  },
  SnackbarContent: {
    bg: 'rgb(50, 50, 50)',
    color: '#fff',
  },
  SpeedDialAction: {
    fabHoverBg: 'rgb(216, 216, 216)',
  },
  StepConnector: {
    border: 'var(--palette-grey-400, #C4CDD5)',
  },
  StepContent: {
    border: 'var(--palette-grey-400, #C4CDD5)',
  },
  Switch: {
    defaultColor: 'var(--palette-common-white, #FFFFFF)',
    defaultDisabledColor: 'var(--palette-grey-100, #F9FAFB)',
    primaryDisabledColor: 'rgb(183, 190, 184)',
    secondaryDisabledColor: 'rgb(212, 177, 255)',
    errorDisabledColor: 'rgb(238, 172, 176)',
    infoDisabledColor: 'rgb(158, 209, 220)',
    successDisabledColor: 'rgb(160, 220, 193)',
    warningDisabledColor: 'rgb(243, 218, 158)',
  },
  TableCell: {
    border: 'rgba(241, NaN, NaN, 1)',
  },
  Tooltip: {
    bg: 'rgba(69, 79, 91, 0.92)',
  },
  dividerChannel: '145',
  colorScheme: 'light',
}

export const palette: Record<ThemeColorScheme, ColorSystemOptions['palette']> = {
  light: {
    ...basePalette,
    text: text.light,
    background: background.light,
    action: action.light,
  },
  dark: {
    ...basePalette,
    text: text.dark,
    background: background.dark,
    action: action.dark,
  },
}
